import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Upload, 
  Search, 
  Trash2,
  Mail,
  MailX, 
  Users,
  FileSpreadsheet,
  Download,
  Star,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Tag,
  Zap,
  Loader2
} from "lucide-react";
import { OutscraperSearch } from "../components/OutscraperSearch";
import { TagManager, TagBadge } from "../components/TagManager";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AddContactDialog,
  EditContactDialog,
  BulkTagDialog,
  ColumnMappingDialog,
  DeleteContactDialog,
  DeleteAllContactsDialog,
  BulkDeleteContactsDialog,
  REQUIRED_FIELDS
} from "../components/contacts";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const INITIAL_CONTACT_STATE = {
  email: "",
  name: "",
  first_name: "",
  last_name: "",
  business_name: "",
  location: "",
  address: "",
  postcode: "",
  category: "",
  website: "",
  phone_number: "",
  company_phone_number: "",
  tags: [],
};

export const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const fileInputRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Email filter state
  const [showOnlyWithEmail, setShowOnlyWithEmail] = useState(false);
  const [totalWithEmail, setTotalWithEmail] = useState(0);
  const [totalWithoutEmail, setTotalWithoutEmail] = useState(0);

  // Selection state
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectingAll, setSelectingAll] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  // Contact form states
  const [contactToDelete, setContactToDelete] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [newContact, setNewContact] = useState(INITIAL_CONTACT_STATE);
  const [editingContact, setEditingContact] = useState(null);
  const [editContactData, setEditContactData] = useState(INITIAL_CONTACT_STATE);

  // Tag states
  const [tags, setTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState([]);
  const [bulkTagIds, setBulkTagIds] = useState([]);

  // Sequence enrollment states
  const [sequences, setSequences] = useState([]);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [enrollingToSequence, setEnrollingToSequence] = useState(false);

  // Column mapping states
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [pendingFile, setPendingFile] = useState(null);

  // Data fetching
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("page_size", pageSize.toString());
      if (searchQuery) params.append("search", searchQuery);
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (showOnlyWithEmail) params.append("has_email", "true");
      if (selectedTagFilter.length > 0) params.append("tags", selectedTagFilter.join(","));
      
      const response = await apiClient.get(`/contacts?${params.toString()}`);
      setContacts(response.data.contacts);
      setTotalContacts(response.data.total);
      setTotalPages(response.data.total_pages);
      setTotalWithEmail(response.data.total_with_email || 0);
      setTotalWithoutEmail(response.data.total_without_email || 0);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, selectedCategory, showOnlyWithEmail, selectedTagFilter]);

  const fetchTags = async () => {
    try {
      const response = await apiClient.get("/tags");
      setTags(response.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get("/contacts/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSequences = async () => {
    try {
      const response = await apiClient.get("/sequences");
      setSequences(response.data);
    } catch (error) {
      console.error("Error fetching sequences:", error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchCategories();
    fetchTags();
    fetchSequences();
  }, [fetchContacts]);

  useEffect(() => {
    setSelectedContacts([]);
  }, [currentPage, contacts]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedCategory]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectedContacts(checked ? contacts.map(c => c.id) : []);
  };

  const handleSelectContact = (contactId, checked) => {
    setSelectedContacts(prev => 
      checked ? [...prev, contactId] : prev.filter(id => id !== contactId)
    );
  };

  const isAllSelected = contacts.length > 0 && selectedContacts.length === contacts.length;
  const isSomeSelected = selectedContacts.length > 0 && selectedContacts.length < contacts.length;

  // CRUD handlers
  const handleSelectAllMatching = async () => {
    setSelectingAll(true);
    try {
      // Build query params matching current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (showOnlyWithEmail) params.append("has_email", "true");
      if (selectedTagFilter.length > 0) params.append("tags", selectedTagFilter.join(","));
      
      const response = await apiClient.get(`/contacts/ids?${params.toString()}`);
      const allIds = response.data.ids || [];
      
      setSelectedContacts(allIds);
      toast.success(`Selected all ${allIds.length} matching contacts`);
    } catch (error) {
      toast.error("Failed to select all contacts");
    } finally {
      setSelectingAll(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    try {
      const response = await apiClient.post("/contacts/bulk-delete", { contact_ids: selectedContacts });
      toast.success(`Deleted ${response.data.deleted} contacts`);
      setSelectedContacts([]);
      setBulkDeleteDialogOpen(false);
      fetchContacts();
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete contacts");
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      toast.error("Email is required");
      return;
    }
    setSavingContact(true);
    try {
      await apiClient.post("/contacts", newContact);
      toast.success("Contact added successfully");
      setAddContactDialogOpen(false);
      setNewContact(INITIAL_CONTACT_STATE);
      fetchContacts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setEditContactData({
      email: contact.email || "",
      name: contact.name || "",
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      business_name: contact.business_name || "",
      location: contact.location || "",
      address: contact.address || "",
      postcode: contact.postcode || "",
      category: contact.category || "",
      website: contact.website || "",
      phone_number: contact.phone_number || "",
      company_phone_number: contact.company_phone_number || "",
      tags: contact.tags || [],
    });
    setEditContactDialogOpen(true);
  };

  const handleEditContact = async () => {
    if (!editContactData.email) {
      toast.error("Email is required");
      return;
    }
    setSavingContact(true);
    try {
      await apiClient.put(`/contacts/${editingContact.id}`, editContactData);
      toast.success("Contact updated successfully");
      setEditContactDialogOpen(false);
      setEditingContact(null);
      fetchContacts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update contact");
    } finally {
      setSavingContact(false);
    }
  };

  const handleBulkTagAssign = async () => {
    if (selectedContacts.length === 0 || bulkTagIds.length === 0) {
      toast.error(selectedContacts.length === 0 ? "No contacts selected" : "No tags selected");
      return;
    }
    try {
      await apiClient.post("/tags/bulk-assign", { contact_ids: selectedContacts, tag_ids: bulkTagIds });
      toast.success(`Tags assigned to ${selectedContacts.length} contacts`);
      setBulkTagDialogOpen(false);
      setBulkTagIds([]);
      setSelectedContacts([]);
      fetchContacts();
    } catch (error) {
      toast.error("Failed to assign tags");
    }
  };

  // Add selected/filtered contacts to a sequence
  const openSequenceDialog = () => {
    if (selectedContacts.length === 0) {
      toast.error("Please select contacts first");
      return;
    }
    setSelectedSequenceId("");
    setSequenceDialogOpen(true);
  };

  const handleAddToSequence = async () => {
    if (!selectedSequenceId || selectedContacts.length === 0) {
      toast.error("Please select a sequence");
      return;
    }
    
    setEnrollingToSequence(true);
    try {
      const response = await apiClient.post(`/sequences/${selectedSequenceId}/enroll`, {
        contact_ids: selectedContacts
      });
      
      const { enrolled, skipped } = response.data;
      if (enrolled > 0) {
        toast.success(`Added ${enrolled} contacts to sequence${skipped > 0 ? ` (${skipped} already enrolled)` : ""}`);
      } else {
        toast.info(`All ${skipped} contacts were already enrolled in this sequence`);
      }
      
      setSequenceDialogOpen(false);
      setSelectedContacts([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contacts to sequence");
    } finally {
      setEnrollingToSequence(false);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await apiClient.delete(`/contacts/${contactToDelete}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const handleDeleteAll = async () => {
    try {
      const response = await apiClient.delete("/contacts/all");
      toast.success(`Deleted ${response.data.deleted} contacts`);
      setContacts([]);
      setCategories([]);
      fetchContacts(); // Refresh to get updated counts
    } catch (error) {
      toast.error("Failed to delete contacts: " + (error.response?.data?.detail || error.message));
    }
    setDeleteAllDialogOpen(false);
  };

  // File upload handlers
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPendingFile(file);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post("/contacts/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setPreviewData(response.data);
      
      // Auto-map columns
      const autoMapping = {};
      REQUIRED_FIELDS.forEach(field => {
        const matchingCol = response.data.columns.find(col => 
          col.toLowerCase().replace(/[^a-z]/g, '') === field.key.replace(/_/g, '') ||
          col.toLowerCase().replace(/[^a-z]/g, '').includes(field.key.replace(/_/g, ''))
        );
        if (matchingCol) autoMapping[field.key] = matchingCol;
      });
      setColumnMapping(autoMapping);
      setMappingDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to read file");
      setPendingFile(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUploadWithMapping = async () => {
    if (!pendingFile) return;

    const unmappedRequired = REQUIRED_FIELDS.filter(f => f.required && !columnMapping[f.key]);
    if (unmappedRequired.length > 0) {
      toast.error(`Please map required field: ${unmappedRequired.map(f => f.label).join(", ")}`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", pendingFile);
      formData.append("mapping", JSON.stringify(columnMapping));

      const response = await apiClient.post("/contacts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Added ${response.data.added} contacts (${response.data.skipped} duplicates skipped)`);
      handleCloseMappingDialog();
      fetchContacts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseMappingDialog = () => {
    setPendingFile(null);
    setPreviewData(null);
    setColumnMapping({});
  };

  const downloadTemplate = () => {
    const csvContent = "email,first_name,last_name,name,business_name,address,location,postcode,category,website,phone_number,company_phone_number\njohn@example.com,John,Smith,John Smith,Smith Plumbing,123 Main St,London,SW1A 1AA,Plumber,www.smithplumbing.com,07700900123,02071234567\njane@example.com,Jane,Doe,Jane Doe,Doe Electrics,456 High St,Manchester,M1 1AE,Electrician,www.doeelectrics.com,07700900456,01234568";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Tag toggle handler for bulk dialog
  const handleTagToggle = (tagId, checked) => {
    setBulkTagIds(prev => checked ? [...prev, tagId] : prev.filter(id => id !== tagId));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">Contacts</h1>
          <p className="text-slate-500 mt-1">Manage your business contacts</p>
        </div>
        <Button
          onClick={() => setAddContactDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-contact-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Outscraper Search & Upload Options */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OutscraperSearch onSearchComplete={fetchContacts} />
        
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-600" />
              Upload Spreadsheet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-500">
              Import contacts from a CSV or Excel file with column mapping
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="border-slate-300 flex-1"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.xlsx,.xls"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-slate-900 hover:bg-slate-800 flex-1"
                data-testid="upload-contacts-btn"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or business..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-contacts-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.filter(cat => cat && cat.trim() !== "").map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Email Filter Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <Switch
                id="email-filter"
                checked={showOnlyWithEmail}
                onCheckedChange={(checked) => {
                  setShowOnlyWithEmail(checked);
                  setCurrentPage(1);
                }}
                data-testid="email-filter-toggle"
              />
              <Label htmlFor="email-filter" className="text-sm cursor-pointer flex items-center gap-1.5">
                {showOnlyWithEmail ? (
                  <>
                    <Mail className="w-4 h-4 text-emerald-600" />
                    <span>With email only</span>
                  </>
                ) : (
                  <span>Show all</span>
                )}
              </Label>
              <span className="text-xs text-slate-500 ml-1">
                ({totalWithEmail} with / {totalWithoutEmail} without)
              </span>
            </div>

            {/* Tag Filter */}
            {tags.length > 0 && (
              <Select 
                value={selectedTagFilter.length === 0 ? "all" : selectedTagFilter[0]}
                onValueChange={(value) => {
                  setSelectedTagFilter(value === "all" ? [] : [value]);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-40 h-9" data-testid="tag-filter-select">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-4 h-4" />
                    <SelectValue placeholder="Filter by tag" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              variant="outline"
              onClick={() => setTagManagerOpen(true)}
              className="flex items-center gap-1.5"
              data-testid="manage-tags-btn"
            >
              <Tag className="w-4 h-4" />
              Manage Tags
            </Button>
            
            {selectedContacts.length > 0 ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={openSequenceDialog}
                  className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                  data-testid="add-to-sequence-btn"
                >
                  <Zap className="w-4 h-4" />
                  Add to Sequence ({selectedContacts.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBulkTagDialogOpen(true)}
                  className="flex items-center gap-1.5"
                  data-testid="bulk-tag-btn"
                >
                  <Tag className="w-4 h-4" />
                  Tag ({selectedContacts.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete ({selectedContacts.length})
                </Button>
              </div>
            ) : totalContacts > 0 && (
              <div className="flex items-center gap-2">
                {/* Select All Matching button - show when filters are active and more contacts exist */}
                {totalContacts > contacts.length && (
                  <Button
                    variant="outline"
                    onClick={handleSelectAllMatching}
                    disabled={selectingAll}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                    data-testid="select-all-matching-btn"
                  >
                    {selectingAll ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Selecting...
                      </span>
                    ) : (
                      <>
                        <Users className="w-4 h-4 mr-2" />
                        Select All {totalContacts} Matching
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setDeleteAllDialogOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="delete-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete All
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5" />
              {totalContacts} Contact{totalContacts !== 1 ? "s" : ""}
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Show:</span>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-20 h-8" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No contacts yet</h3>
              <p className="text-slate-500 text-center max-w-md mb-4">
                Upload a spreadsheet and map columns to import your contacts
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Spreadsheet
              </Button>
            </div>
          ) : (
            <>
              {/* Selection info bar */}
              {selectedContacts.length > 0 && (
                <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center justify-between">
                  <span className="text-sm text-blue-700">
                    <strong>{selectedContacts.length}</strong> contact{selectedContacts.length !== 1 ? "s" : ""} selected
                    {selectedContacts.length > contacts.length && (
                      <span className="text-blue-500 ml-1">
                        (across all pages)
                      </span>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedContacts([])}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                  >
                    Clear selection
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={isAllSelected}
                          ref={(el) => { if (el) el.indeterminate = isSomeSelected; }}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-slate-600">Business</TableHead>
                      <TableHead className="font-semibold text-slate-600">Email</TableHead>
                      <TableHead className="font-semibold text-slate-600">Tags</TableHead>
                      <TableHead className="font-semibold text-slate-600">Phone</TableHead>
                      <TableHead className="font-semibold text-slate-600">Location</TableHead>
                      <TableHead className="font-semibold text-slate-600">Category</TableHead>
                      <TableHead className="font-semibold text-slate-600">Rating</TableHead>
                      <TableHead className="font-semibold text-slate-600 w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow 
                        key={contact.id} 
                        className={`table-row-hover ${selectedContacts.includes(contact.id) ? 'bg-blue-50' : ''}`} 
                        data-testid={`contact-row-${contact.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedContacts.includes(contact.id)}
                            onCheckedChange={(checked) => handleSelectContact(contact.id, checked)}
                            data-testid={`select-contact-${contact.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          <div>
                            <p>{contact.business_name || '-'}</p>
                            {(contact.name || contact.first_name || contact.last_name) && (
                              <p className="text-xs text-slate-500">
                                {contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {contact.email ? (
                            <div className="flex flex-col">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3 text-emerald-500" />
                                {contact.email}
                              </span>
                              {contact.unsubscribed && (
                                <span className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                                  <MailX className="w-3 h-3" />
                                  Unsubscribed
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="flex items-center gap-1 text-slate-400">
                              <MailX className="w-3 h-3 text-slate-400" />
                              <span className="italic">No email</span>
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-32">
                            {contact.tags?.length > 0 ? (
                              contact.tags.slice(0, 3).map(tagId => {
                                const tag = tags.find(t => t.id === tagId);
                                return tag ? <TagBadge key={tag.id} tag={tag} small /> : null;
                              })
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                            {contact.tags?.length > 3 && (
                              <span className="text-xs text-slate-500">+{contact.tags.length - 3}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">{contact.phone_number || contact.company_phone_number || '-'}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{contact.location || '-'}</TableCell>
                        <TableCell>
                          {contact.category ? (
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                              {contact.category}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {contact.outscraper_data?.rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                              <span className="text-sm text-slate-700">{contact.outscraper_data.rating}</span>
                              <span className="text-xs text-slate-400">({contact.outscraper_data.reviews_count || 0})</span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(contact)}
                              className="text-slate-400 hover:text-blue-600"
                              data-testid={`edit-contact-${contact.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setContactToDelete(contact.id);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-slate-400 hover:text-red-600"
                              data-testid={`delete-contact-${contact.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalContacts)} of {totalContacts} contacts
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 px-3"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-slate-900' : ''}`}
                            data-testid={`page-${pageNum}-btn`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="h-8 px-3"
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ColumnMappingDialog
        open={mappingDialogOpen}
        onOpenChange={setMappingDialogOpen}
        previewData={previewData}
        columnMapping={columnMapping}
        onColumnMappingChange={setColumnMapping}
        onUpload={handleUploadWithMapping}
        uploading={uploading}
        onClose={handleCloseMappingDialog}
      />

      <DeleteContactDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
      />

      <DeleteAllContactsDialog
        open={deleteAllDialogOpen}
        onOpenChange={setDeleteAllDialogOpen}
        totalContacts={totalContacts}
        onConfirm={handleDeleteAll}
      />

      <BulkDeleteContactsDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedContacts.length}
        onConfirm={handleBulkDelete}
      />

      <AddContactDialog
        open={addContactDialogOpen}
        onOpenChange={setAddContactDialogOpen}
        contactData={newContact}
        onContactDataChange={setNewContact}
        onSave={handleAddContact}
        saving={savingContact}
      />

      <EditContactDialog
        open={editContactDialogOpen}
        onOpenChange={setEditContactDialogOpen}
        contactData={editContactData}
        onContactDataChange={setEditContactData}
        onSave={handleEditContact}
        saving={savingContact}
        tags={tags}
      />

      <TagManager
        open={tagManagerOpen}
        onOpenChange={setTagManagerOpen}
        onTagsUpdated={fetchTags}
      />

      <BulkTagDialog
        open={bulkTagDialogOpen}
        onOpenChange={setBulkTagDialogOpen}
        selectedContactCount={selectedContacts.length}
        tags={tags}
        selectedTagIds={bulkTagIds}
        onTagToggle={handleTagToggle}
        onAssign={handleBulkTagAssign}
      />

      {/* Add to Sequence Dialog */}
      <Dialog open={sequenceDialogOpen} onOpenChange={setSequenceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              Add to Sequence
            </DialogTitle>
            <DialogDescription>
              Add {selectedContacts.length} selected contact{selectedContacts.length !== 1 ? "s" : ""} to an email sequence
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="sequence-select" className="text-sm font-medium">
              Select Sequence
            </Label>
            <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
              <SelectTrigger className="mt-1.5" id="sequence-select" data-testid="sequence-select">
                <SelectValue placeholder="Choose a sequence..." />
              </SelectTrigger>
              <SelectContent>
                {sequences.length === 0 ? (
                  <div className="px-2 py-4 text-center text-sm text-slate-500">
                    No sequences created yet.
                    <br />
                    <a href="/sequences" className="text-blue-600 hover:underline">
                      Create one first →
                    </a>
                  </div>
                ) : (
                  sequences.map((seq) => (
                    <SelectItem key={seq.id} value={seq.id}>
                      <div className="flex items-center gap-2">
                        <span>{seq.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          seq.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {seq.status}
                        </span>
                        <span className="text-xs text-slate-400">
                          ({seq.steps?.length || 0} steps)
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {selectedSequenceId && sequences.find(s => s.id === selectedSequenceId)?.status !== 'active' && (
              <p className="mt-2 text-sm text-amber-600">
                ⚠️ This sequence is not active. Contacts will be enrolled but won't receive emails until you activate the sequence.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSequenceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddToSequence}
              disabled={!selectedSequenceId || enrollingToSequence}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-add-to-sequence-btn"
            >
              {enrollingToSequence ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </span>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Add to Sequence
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
